import React, { Component } from 'react';
import { connect } from 'react-redux';

// Types and Enums
import { ReduxState } from '../../../redux/modules/types';
import { TwoFactorEnableSteps } from '../../../redux/modules/types/two-factor-auth';

// Actions
import { deleteMFA, generateBackupCode, showModal } from '../../../redux/modules/two-factor-auth';
import { getMe } from '../../../redux/modules/auth';

// Utils
import { formatMessage, FormattedMessage } from '../../../utils/i18n';
import notification from '../../../utils/notification';

// Components
import { DestructiveButton, PrimaryButton } from '../../components/buttons';
import TwoFactorProtectedAction from '../auth/2fa/TwoFactorProtectedAction';
import ExternalLink from '../../components/ExternalLink';
import Locale from '@/types/locale';
import User from '@/types/user';

type StateProps = {
  locale: Locale;
};

type DispatchProps = {
  deleteMFA: (otp: string) => Promise<void>;
  generateBackupCode: (otp: string) => Promise<void>;
  getMe: (contextId?: string) => Promise<User>;
  showModal: (startStep?: TwoFactorEnableSteps) => void;
};

type Props = StateProps &
  DispatchProps & {
    user: User;
  };
class LegacySecurityTwoFactor extends Component<Props> {
  disableTwoFactor = async (otp: string) => {
    await this.props.deleteMFA(otp);
    await this.props.getMe();
    notification.success(
      formatMessage('TwoFactorAuthentication.Notifications.Disabled.Description'),
      formatMessage('TwoFactorAuthentication.Notifications.Disabled.Title'),
    );
  };

  renderEnable = () => {
    const { locale } = this.props;

    return (
      <div className="two-factor-auth-enable">
        <h4>
          <FormattedMessage id="Settings.Security.TwoFactor.Enable.Title" />
        </h4>
        <p>
          <FormattedMessage id="Settings.Security.TwoFactor.Enable.Description" />
        </p>
        <div className="actions">
          <PrimaryButton className="d-block" onClick={() => this.props.showModal(TwoFactorEnableSteps.BEGIN)}>
            <FormattedMessage id="Settings.Security.TwoFactor.Enable.EnableButton" />
          </PrimaryButton>
          <ExternalLink to={`${import.meta.env.REACT_APP_HELP_SITE_URL}/${locale.toLocaleLowerCase()}/articles/9315570206483`} className="d-block">
            <FormattedMessage id="Settings.Security.TwoFactor.LearnMore" />
          </ExternalLink>
        </div>
      </div>
    );
  };

  renderDisable = () => {
    const {
      locale,
      user: {
        authUser: { mfa },
      },
    } = this.props;

    return (
      <div className="two-factor-auth-disable">
        <h4>
          <FormattedMessage id="Settings.Security.TwoFactor.Disable.Title" />
        </h4>
        <p className="mt-4 mb-4">
          <FormattedMessage id="Settings.Security.TwoFactor.Disable.Description" />
        </p>
        <div className="actions">
          <TwoFactorProtectedAction
            description={formatMessage('Settings.Security.TwoFactor.Disable.ProtectedActions.NewBackup')}
            action={this.props.generateBackupCode}
          >
            {(openModal: () => void) => (
              <PrimaryButton className="me-3" onClick={openModal}>
                <FormattedMessage id="Settings.Security.TwoFactor.Disable.BackupCodeButton" />
              </PrimaryButton>
            )}
          </TwoFactorProtectedAction>
          <ExternalLink to={`${import.meta.env.REACT_APP_HELP_SITE_URL}/${locale.toLocaleLowerCase()}/articles/9315570206483`} className="d-block">
            <FormattedMessage id="Settings.Security.TwoFactor.LearnMore" />
          </ExternalLink>
          {!mfa?.required && (
            <TwoFactorProtectedAction
              description={formatMessage('Settings.Security.TwoFactor.Disable.ProtectedActions.DisableTwoFactor')}
              action={this.disableTwoFactor}
            >
              {(openModal: () => void) => (
                <DestructiveButton className="disable-two-factor with-border" onClick={openModal}>
                  <FormattedMessage id="Settings.Security.TwoFactor.Disable.DisableButton" />
                </DestructiveButton>
              )}
            </TwoFactorProtectedAction>
          )}
        </div>
      </div>
    );
  };

  render() {
    const {
      user: {
        authUser: { mfa },
      },
    } = this.props;

    if (mfa?.active) {
      return this.renderDisable();
    }

    return this.renderEnable();
  }
}

const mapStateToProps = (state: ReduxState): StateProps => ({
  locale: state.intl.locale,
});

export default connect(mapStateToProps, { deleteMFA, generateBackupCode, getMe, showModal })(LegacySecurityTwoFactor);
